// src/context/AuthContext.jsx
import { createContext, useContext, useState } from "react";
import APICall, { setAuthTokens } from "../APICalls/APICall";

const AuthContext = createContext();

function safeParse(key, fallback = null) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
}

export const AuthProvider = ({ children }) => {
    const [user,     setUser]     = useState(() => safeParse("user"));
    const [menus,    setMenus]    = useState(() => safeParse("menus", []));
    const [org,      setOrg]      = useState(() => safeParse("org"));
    const [roleName, setRoleName] = useState(() => localStorage.getItem("role_name") || "");
    const [profileType, setProfileType] = useState(() => localStorage.getItem("profile_type") || "employee");
    // Billing lifecycle from the login response (ACTIVE / GRACE), for the renew banner.
    const [subscription, setSubscription] = useState(() => safeParse("subscription"));
    // Super-admin "act as org": the org being impersonated, or null.
    // `impersonating_org` lives in localStorage (persists across browser
    // restarts) but the tenant access token lives in sessionStorage (cleared on
    // close). A stale org with no token would make the embedded tenant pages
    // (Intents / KB / Flow Builder / Channels) fire authenticated calls with an
    // empty token → 401 → forced logout to the tenant login. Only treat a
    // persisted impersonation as active while its tenant token is still present.
    const [impersonating, setImpersonating] = useState(() => {
        const savedOrg = safeParse("impersonating_org");
        if (savedOrg && !sessionStorage.getItem("token")) {
            localStorage.removeItem("impersonating_org");
            return null;
        }
        return savedOrg;
    });

    const login = (data) => {
        // Access + rotating refresh token in sessionStorage (cleared on browser close)
        setAuthTokens(data.token, data.refresh_token);
        localStorage.setItem("user",  JSON.stringify(data.user));
        localStorage.setItem("menus", JSON.stringify(data.menus));

        setUser(data.user);
        setMenus(data.menus);

        if (data.org) {
            localStorage.setItem("org", JSON.stringify(data.org));
            setOrg(data.org);
        }
        if (data.role_name) {
            localStorage.setItem("role_name", data.role_name);
            setRoleName(data.role_name);
        }
        const pt = data.profile_type || "employee";
        localStorage.setItem("profile_type", pt);
        setProfileType(pt);
        if (data.subscription) {
            localStorage.setItem("subscription", JSON.stringify(data.subscription));
            setSubscription(data.subscription);
        }
        // Let ThemeProvider react (agents are forced to the light theme).
        window.dispatchEvent(new Event("cvq-profile-change"));
    };

    // Clear the tenant session locally (no server-side revocation). Shared by
    // both normal logout and exit-impersonation.
    const clearLocalSession = () => {
        localStorage.removeItem("user");
        localStorage.removeItem("menus");
        localStorage.removeItem("org");
        localStorage.removeItem("role_name");
        localStorage.removeItem("profile_type");
        localStorage.removeItem("subscription");
        localStorage.removeItem("impersonating_org");
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("refresh_token");
        setUser(null);
        setMenus([]);
        setOrg(null);
        setRoleName("");
        setProfileType("employee");
        setSubscription(null);
        setImpersonating(null);
        window.dispatchEvent(new Event("cvq-profile-change"));
    };

    const logout = async () => {
        // Revoke tokens server-side (best-effort) + clear access/refresh tokens.
        await APICall.serverLogout();
        clearLocalSession();
    };

    // Super-admin enters "act as org": populate the tenant session from an
    // impersonation payload and remember which org we're acting as.
    const enterImpersonation = (data) => {
        login(data);
        if (data.org) {
            localStorage.setItem("impersonating_org", JSON.stringify(data.org));
            setImpersonating(data.org);
        }
    };

    // Exit "act as org": clear the tenant session WITHOUT server logout so the
    // real org admin's tokens are never revoked. The super-admin's sa_token is
    // untouched, so the management console remains authenticated.
    const exitImpersonation = () => {
        clearLocalSession();
    };

    return (
        <AuthContext.Provider value={{
            user, menus, org, roleName, profileType, subscription, impersonating,
            login, logout, enterImpersonation, exitImpersonation,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

export const usePermission = (path) => {
    const { menus } = useAuth();

    if (!path || path === "/Dashboard" || path === "/") {
        return { canView: true, canAdd: true, canEdit: true, canDelete: true };
    }

    const normalizedPath = path.endsWith("/") ? path.slice(0, -1) : path;
    const menu = menus.find(m => m.path === normalizedPath || m.path === path);

    if (!menu?.permissions) {
        return { canView: false, canAdd: false, canEdit: false, canDelete: false };
    }

    return {
        canView:   !!menu.permissions.view,
        canAdd:    !!menu.permissions.add,
        canEdit:   !!menu.permissions.edit,
        canDelete: !!menu.permissions.delete,
    };
};
